import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, Check } from "lucide-react";
import { easeOut } from "@/components/Reveal";

const lines = [
  { text: "The invisible threat", cls: "text-navy" },
  { text: "in your home —", cls: "text-navy" },
  { text: "handled.", cls: "text-gold-deep italic" },
];

const trust = ["Licensed & insured", "8 years in business", "Free quotes, always"];

export default function Hero() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section className="grain relative overflow-hidden bg-limestone">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_75%_20%,rgba(2,132,199,0.08),transparent_70%)]"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="mb-6 inline-flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
          >
            <span className="h-px w-8 bg-gold-deep" aria-hidden="true" />
            Home Defense Solutions LLC — Boston, MA
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.85, delay: 0.15 + i * 0.13, ease: easeOut }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: easeOut }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-slate-600"
          >
            Certified radon testing and engineered mitigation systems for Greater
            Boston and MetroWest homes. Eight years. One focus: the air in your
            home.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: easeOut }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-book-test-btn"
              href="#booking"
              className="rounded-lg bg-navy px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-navy/20 transition-all hover:-translate-y-0.5 hover:bg-navy-deep"
            >
              Book a Radon Test
            </a>
            <a
              data-testid="hero-free-quote-btn"
              href="#booking"
              className="rounded-lg border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-all hover:-translate-y-0.5 hover:border-gold-deep hover:text-gold-deep"
            >
              Request a Free Quote
            </a>
            <a
              data-testid="hero-phone-cta"
              href="tel:+15089692104"
              className="flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-600 transition-colors hover:text-gold-deep"
            >
              <Phone className="h-4 w-4" />
              (508) 969-2104
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {trust.map((t) => (
              <li
                key={t}
                className="flex items-center gap-2 text-sm font-medium text-slate-500"
              >
                <Check className="h-4 w-4 text-gold-deep" />
                {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5" ref={imgRef}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: easeOut }}
            className="relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-3 rounded-2xl border border-gold/50"
            />
            <motion.div style={{ y }} className="relative overflow-hidden rounded-xl shadow-2xl shadow-navy/25">
              <img
                src="https://images.pexels.com/photos/5140629/pexels-photo-5140629.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=940"
                alt="Classic Boston brownstone home in the evening light"
                className="h-[420px] w-full object-cover lg:h-[520px]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-[radial-gradient(90%_60%_at_30%_20%,transparent,rgba(10,37,64,0.55))]"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05, ease: easeOut }}
              className="absolute -left-4 top-8 flex items-center gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-left-8"
            >
              <ShieldCheck className="h-8 w-8 text-gold-deep" />
              <div>
                <p className="font-display text-xl font-bold leading-none text-navy">
                  8 Years
                </p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  Protecting Boston homes
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2, ease: easeOut }}
              className="absolute -bottom-6 right-4 w-52 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur sm:-right-5"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                EPA action level
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-navy">
                4.0 <span className="text-sm font-semibold text-slate-500">pCi/L</span>
              </p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full w-1/2 rounded-full bg-gradient-to-r from-sky-500 via-amber-500 to-red-500" />
              </div>
              <p className="mt-2 text-[11px] leading-snug text-slate-500">
                At or above this level, the EPA recommends mitigation.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
